// src/Routes/control.tsx
import React from 'react';
import { Navigate } from 'react-router-dom';
import { withLoading } from '@app/hocs/withLoading.hoc';
import type { RouteConfig } from '@app/components/header/components/Breadcrumb/DynamicBreadcrumb';
import { getAuthAndLogout, protectedLayout, sharedRootChildren } from './shared';
import InvalidPermission from '@app/pages/ErrorInvalidPermission'; // Adjust the path to your `InvalidPermission` component

// lazy imports
const UserList = withLoading(React.lazy(() => import('@app/panels/control/pages/AdminUsers/AdminUserListPage')));
const UserAdd = withLoading(React.lazy(() => import('@app/panels/control/pages/AdminUsers/AdminUserAddPage')));
const UserEdit = withLoading(React.lazy(() => import('@app/panels/control/pages/AdminUsers/AdminUserEditPage')));
const ChangePassword = withLoading(React.lazy(() => import('@app/pages/ChangePasswords/ChangePasswordPages')));

const EndUserList = withLoading(React.lazy(() => import('@app/panels/control/pages/endUsers/EndUserListPage')));
const EndUserAdd = withLoading(React.lazy(() => import('@app/panels/control/pages/endUsers/EndUserAddPage')));
const EndUserEdit = withLoading(React.lazy(() => import('@app/panels/control/pages/endUsers/EndUserEditPage')));


const UserRolesList = withLoading(React.lazy(() => import('@app/pages/UserRoles/UserRolesListPage')));
const UserRolesAdd = withLoading(React.lazy(() => import('@app/pages/UserRoles/UserRolesAddPage')));
const UserRolesEdit = withLoading(React.lazy(() => import('@app/pages/UserRoles/UserRolesEditPage')));

const ReportingManageInfoList = withLoading(React.lazy(() => import('@app/pages/Reportings/ReportingManageInfoListPages')));
const ReportingManageInfo = withLoading(React.lazy(() => import('@app/pages/Reportings/ReportingManageInfoPages')));
const ReportingCategoryAdd = withLoading(React.lazy(() => import('@app/pages/Reportings/ReportingCategoryAddPages')));
const ReportingDetailsList = withLoading(React.lazy(() => import('@app/pages/Reportings/ReportingDetailsListPages')));
const ReportingDetailsAdd = withLoading(React.lazy(() => import('@app/pages/Reportings/ReportingDetailsAddPages')));
const ReportingCategoryEdit = withLoading(React.lazy(() => import('@app/pages/Reportings/ReportingCategoryEditPages')));


const SealTypesListPage = withLoading(React.lazy(() => import('@app/panels/control/pages/sealTypes/SealTypesListPage')));
const SealTypesAddPage = withLoading(React.lazy(() => import('@app/panels/control/pages/sealTypes/SealTypesAddPage')));
const SealTypesEditPage = withLoading(React.lazy(() => import('@app/panels/control/pages/sealTypes/SealTypesEditPage')));


const SealGroupsListPage = withLoading(React.lazy(() => import('@app/panels/control/pages/sealGroups/SealGroupsListPage')));
const SealGroupMapPage = withLoading(React.lazy(() => import('@app/panels/control/pages/sealGroups/SealGroupMapPage')));

const LuggageBuckleTypesListPage = withLoading(React.lazy(() => import('@app/panels/control/pages/luggageBuckleTypes/LuggageBuckleTypesListPage')));
const LuggageBuckleTypesAddPage = withLoading(React.lazy(() => import('@app/panels/control/pages/luggageBuckleTypes/LuggageBuckleTypesAddPage')));
const LuggageBuckleTypesEditPage = withLoading(React.lazy(() => import('@app/panels/control/pages/luggageBuckleTypes/LuggageBuckleTypesEditPage')));

const FactoryPrintingListPage = withLoading(React.lazy(() => import('@app/panels/control/pages/factoryPrintings/FactoryPrintingListPage')));
const GenerateFactoryBatchListPage = withLoading(React.lazy(() => import('@app/panels/control/pages/factoryPrintings/GenerateFactoryBatchListPage')));
const FactoryScanningListPage = withLoading(React.lazy(() => import('@app/panels/control/pages/factoryScannings/FactoryScanningListPage')));

export const controlRoutes: RouteConfig[] = [
  {

    path: '/',
    element: protectedLayout,
    breadcrumb: 'Dashboard',
    children: [
      ...sharedRootChildren,

      // // users
      {
        path: 'users',
        breadcrumb: 'Users',
        children: [
          { path: "add", element: <UserAdd />, breadcrumb: "Add User" },
          { path: 'edit/:id', element: <UserEdit />, breadcrumb: 'Edit User' },
          { index: true, element: <UserList />, breadcrumb: 'Users' },
          { path: 'ChangePassword', element: <ChangePassword />, breadcrumb: 'ChangePassword' },
        ],
      },
      {
        path: 'endUsers',
        breadcrumb: 'End Users',
        children: [
          { path: "add", element: <EndUserAdd />, breadcrumb: "Add User" },
          { path: 'edit/:id', element: <EndUserEdit />, breadcrumb: 'Edit User' },
          { index: true, element: <EndUserList />, breadcrumb: 'Users' },
          { path: 'ChangePassword', element: <ChangePassword />, breadcrumb: 'ChangePassword' },
        ],
      },
      // user-roles
      {
        path: 'user-roles',
        breadcrumb: 'User Roles',
        children: [
          { path: 'add', element: <UserRolesAdd />, breadcrumb: 'Add User Role' },
          { path: 'edit/:id', element: <UserRolesEdit />, breadcrumb: 'Edit User Role' },
          { index: true, element: <UserRolesList />, breadcrumb: 'User Roles' },
        ],
      },
      {
        path: "sealTypes",
        breadcrumb: "Seal Types",
        children: [
          { path: "list", element: <SealTypesListPage />, breadcrumb: "List" },
          { path: "sealTypes-add", element: <SealTypesAddPage />, breadcrumb: "Add Seal Type" },
          { path: "sealTypes-edit/:id", element: <SealTypesEditPage />, breadcrumb: "Edit Seal Type" },
        ],
      },
      {
        path: "sealGroups",
        breadcrumb: "Seal Groups",
        children: [
          { path: "list", element: <SealGroupsListPage />, breadcrumb: "List" },
          { path: "sealGroups-map/:id", element: <SealGroupMapPage />, breadcrumb: "Map Seals" },
          // { path: "sealTypes-add", element: <SealTypesAddPage />, breadcrumb: "Add Seal Type" },
          // { path: "sealTypes-edit/:id", element: <SealTypesEditPage />, breadcrumb: "Edit Seal Type" },
        ],
      },
      {
        path: "luggageBuckleTypes",
        breadcrumb: "Luggage Buckle Types",
        children: [
          { path: "list", element: <LuggageBuckleTypesListPage />, breadcrumb: "List" },
          { path: "luggageBuckleTypes-add", element: <LuggageBuckleTypesAddPage />, breadcrumb: "Add Luggage Buckle Type" },
          { path: "luggageBuckleTypes-edit/:id", element: <LuggageBuckleTypesEditPage />, breadcrumb: "Edit Luggage Buckle Type" },
        ],
      },
      {
        path: "factoryPrinting",
        breadcrumb: "Factory Printing",
        children: [
          { path: "printing", element: <FactoryPrintingListPage />, breadcrumb: "printing" },
          { path: "generate", element: <GenerateFactoryBatchListPage />, breadcrumb: "generatePrinting" },
        ],
      },
      {
        path: "factoryScanning",
        breadcrumb: "Factory Scanning",
        children: [
          { path: "list", element: <FactoryScanningListPage />, breadcrumb: "scanning" },
        ],
      },
      // reporting
      {
        path: 'reporting',
        breadcrumb: 'Reporting',
        children: [
          { path: 'reporting/list', element: <ReportingManageInfoList />, breadcrumb: 'reportList' },
          { path: 'reporting-manage/:id', element: <ReportingManageInfo />, breadcrumb: 'Manage Report' },
          { path: 'reporting-category-add', element: <ReportingCategoryAdd />, breadcrumb: 'Add Reporting Category' },
          { path: 'reporting-category-list/:id', element: <ReportingDetailsList />, breadcrumb: 'Reporting Category List' },
          { path: 'reporting-details-add', element: <ReportingDetailsAdd />, breadcrumb: 'Add Reporting Details' },
          { path: 'reporting-category-edit/:id', element: <ReportingCategoryEdit />, breadcrumb: 'Edit Reporting Category' },
        ],
      },
      { path: '*', element: <InvalidPermission />, breadcrumb: 'Invalid Permission' },
    ],
  },

  // Auth + Logout (root-level, **not** nested under protectedLayout)
  ...getAuthAndLogout('control'),

  // Catch-all → redirect to login

];

export default controlRoutes;
